/**
 * AI background removal for an image element.
 *
 * The inference itself is the Remove Background tool's engine
 * (@imgly/background-removal in a worker) — reused rather than re-wrapped so the
 * editor gets the same model, the same progress reporting and the same ~40MB of
 * WASM the tool has already warmed in the browser cache.
 *
 * What is editor-specific is the two ends: an element's `src` can be a data URL,
 * an R2 URL or an inline SVG, and whatever comes back has to go straight back
 * onto the element — so the source is resolved to a Blob here, and the cutout is
 * embedded as a data URL exactly like an upload (see utils/imageFiles.js for why
 * a remote URL cannot be stored directly: it taints the export canvas).
 */

import { runBackgroundRemoval } from '@/pages/user/tools/image/remove-background/removeBackgroundEngine';

/**
 * Vectors are out: the model segments pixels, so an SVG would come back as a
 * flattened raster of whatever size the browser happened to rasterise it at.
 */
export function canRemoveBackground(src) {
    const value = String(src || '').trim();
    if (!value) return false;
    if (/^data:image\/svg\+xml/i.test(value)) return false;
    if (/^https?:\/\/[^?#]*\.svg(?:[?#]|$)/i.test(value)) return false;
    return true;
}

function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Failed to read the cutout'));
        reader.readAsDataURL(blob);
    });
}

/**
 * The element's picture as bytes.
 *
 * Fetched on the main thread rather than handing the worker a URL, so a remote
 * image that the browser refuses to read cross-origin fails here with something
 * an admin can act on, instead of surfacing as an opaque worker error.
 */
async function srcToBlob(src) {
    try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return await res.blob();
    } catch {
        throw new Error("Couldn't read this image. Re-upload it and try again.");
    }
}

/**
 * Cut the subject out of `src`. Resolves to a transparent PNG data URL.
 * `onProgress` gets 0–100 while the model downloads and runs.
 */
export async function removeImageBackground(src, onProgress) {
    if (!canRemoveBackground(src)) throw new Error('This image type is not supported.');
    const blob = await srcToBlob(src);
    const cutout = await runBackgroundRemoval(blob, onProgress);
    return blobToDataURL(cutout);
}
